import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../supabaseClient';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(true);

  useEffect(() => {
    const checkMfaStatus = async () => {
      if (!user) {
        setMfaLoading(false);
        return;
      }

      try {
        // Check if user has active session
        const { data: session } = await supabase.auth.getSession();
        
        if (session?.session) {
          // User has active session, check if they have MFA enabled but not verified
          const { data: factors } = await supabase.auth.mfa.listFactors();
          const verifiedFactors = factors?.all?.filter(factor => factor.status === 'verified') || [];
          
          // If user has MFA factors but no verified session, they need to complete MFA
          if (verifiedFactors.length > 0 && (!session.session.aal || session.session.aal === 'aal1')) {
            // Check if this is actually an MFA-incomplete state
            // In Supabase, aal1 means first factor only, aal2 means MFA completed
            setMfaRequired(true);
          } else {
            setMfaRequired(false);
          }
        } else {
          setMfaRequired(false);
        }
      } catch (error) {
        console.error('Error checking MFA status:', error);
        setMfaRequired(false);
      } finally {
        setMfaLoading(false);
      }
    };

    if (user && !loading) {
      checkMfaStatus();
    } else {
      setMfaLoading(false);
    }
  }, [user, loading]);

  // Show loading indicator while checking authentication and MFA
  if (loading || mfaLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if no user
  if (!user) {
    return <Navigate to="/login" />;
  }

  // Redirect to login if MFA is required but not completed
  // This ensures users can't bypass MFA by directly accessing protected routes
  if (mfaRequired) {
    return <Navigate to="/login" />;
  }

  // User is authenticated and MFA is satisfied
  return children;
};

export default PrivateRoute;